import React from 'react';
import Icon from './icons';
import { getCard, itm, itemMaps } from './collect';
import MapPicker from './MapPicker';
import { TYPES, needsOptions } from './types';
import type { Collect, Item } from './types';

export interface DragSrc { ref: string; j: number; }

interface Props {
  refId: string;
  items: Item[];
  scope: 'person' | 'co';
  /** 포털이 이미 전송된 카드면 항목 삭제 전에 한 번 더 확인 (제출물이 딸려 지워질 수 있다) */
  sent?: boolean;
  edit: (fn: (c: Collect) => void) => void;
  dragSrc: DragSrc | null;
  setDragSrc: (d: DragSrc | null) => void;
}

export default function ItemList({ refId, items, scope, sent, edit, dragSrc, setDragSrc }: Props) {
  const [armedDel, setArmedDel] = React.useState<number | null>(null);
  React.useEffect(() => { if (armedDel == null) return; const t = window.setTimeout(() => setArmedDel(null), 3500); return () => window.clearTimeout(t); }, [armedDel]);
  const removeItem = (j: number) => {
    if (sent && armedDel !== j) { setArmedDel(j); return; }
    setArmedDel(null);
    edit((c) => { getCard(c, refId).items.splice(j, 1); });
  };
  const onDrop = (j: number) => {
    if (!dragSrc || dragSrc.ref !== refId) return;
    const from = dragSrc.j;
    edit((c) => { const a = getCard(c, refId).items; const [m] = a.splice(from, 1); a.splice(j, 0, m); });
    setDragSrc(null);
  };
  const set = (j: number, fn: (x: Item) => void) => edit((c) => { fn(getCard(c, refId).items[j]); });

  return (
    <>
      {items.map((it, j) => (
        <React.Fragment key={it.id}>
          <div className="iitem" draggable onDragStart={() => setDragSrc({ ref: refId, j })} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(j)}>
            <span className="ihandle">⋮⋮</span>
            <input className="ititle" value={it.title} onChange={(e) => set(j, (x) => { x.title = e.target.value; })} />
            <select className="itype" value={it.type} onChange={(e) => set(j, (x) => { x.type = e.target.value; })}>
              {TYPES.map((t) => <option key={t.v} value={t.v}>{t.icon} {t.l}</option>)}
            </select>
            <button className={'itog ' + (it.sensitive ? 'sens' : 'norm')} onClick={() => set(j, (x) => { x.sensitive = !x.sensitive; })}>{it.sensitive ? '민감 ✓' : '민감'}</button>
            <button className={'itog ' + (it.required ? 'req' : 'opt')} onClick={() => set(j, (x) => { x.required = !x.required; })}>{it.required ? '필수 ✓' : '필수'}</button>
            <button className={'idel' + (armedDel === j ? ' armed' : '')} title={sent ? '전송된 포털의 항목입니다 — 제출된 파일이 있으면 포털에는 남고, 여기 목록에서만 빠집니다' : undefined} onClick={() => removeItem(j)}>{armedDel === j ? '한 번 더 누르면 삭제' : '삭제'}</button>
          </div>
          <div className="iexpand">
            <textarea
              className="idesc"
              placeholder="제출 안내 설명글 (예: 특허로 사이트에서 발급 후 업로드해 주세요)"
              value={it.desc}
              onChange={(e) => set(j, (x) => { x.desc = e.target.value; })}
            />
            {needsOptions(it.type) ? (
              <div className="glrow"><span>{it.type === 'radio' ? '◉' : '☑'}</span>
                <input
                  value={(it.options || []).join(', ')}
                  placeholder="선택지를 쉼표로 구분해 입력 (예: 예, 아니오)"
                  onChange={(e) => set(j, (x) => { x.options = e.target.value.split(',').map((v) => v.trim()).filter(Boolean); })}
                />
              </div>
            ) : null}
            <MapPicker
              scope={scope}
              maps={itemMaps(it)}
              onChange={(next) => set(j, (x) => { x.maps = next; x.map = ''; })}
            />
            {it.links.map((lk, k) => (
              <div className="glrow" key={k}><span>🔗</span>
                <input className="glname" value={lk.name} placeholder="링크 이름" onChange={(e) => set(j, (x) => { x.links[k].name = e.target.value; })} />
                <input value={lk.url} placeholder="https://..." onChange={(e) => set(j, (x) => { x.links[k].url = e.target.value; })} />
                <button className="idel" onClick={() => set(j, (x) => { x.links.splice(k, 1); })}>삭제</button>
              </div>
            ))}
            {it.files.map((fl, k) => (
              <div className="glrow" key={k}><span>📎</span>
                <input className="glname2" value={fl.name} placeholder="가이드 파일 이름 (예: 작성예시.pdf)" onChange={(e) => set(j, (x) => { x.files[k].name = e.target.value; })} />
                <button className="idel" onClick={() => set(j, (x) => { x.files.splice(k, 1); })}>삭제</button>
              </div>
            ))}
            <div className="gbtns">
              <button className="gbtn" onClick={() => set(j, (x) => { x.links.push({ name: '', url: '' }); })}>+ 가이드 링크</button>
              <button className="gbtn" onClick={() => set(j, (x) => { x.files.push({ name: '' }); })}>+ 가이드 파일</button>
            </div>
          </div>
        </React.Fragment>
      ))}
      <button className="additem" onClick={() => edit((c) => { getCard(c, refId).items.push(itm('새 항목', 'text')); })}>
        <Icon n="plus" style={{ width: 13, height: 13 }} /> 항목 추가
      </button>
    </>
  );
}
